import { useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { blogApi, type BlogPostSummary } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useTimelineRowMotion } from "@/hooks/useTimelineRowMotion";

const HOME_BLOG_LIMIT = 4;

const useFormatDate = () => {
    const { lang } = useLanguage();
    return (iso?: string) => {
        if (!iso) return "";
        try {
            return new Intl.DateTimeFormat(lang, { year: "numeric", month: "long" }).format(
                new Date(iso),
            );
        } catch {
            return "";
        }
    };
};

const BlogCard = ({ post }: { post: BlogPostSummary }) => {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();
    const formatDate = useFormatDate();
    const categoryName = post.category?.name ?? t("blog_home.default_category");

    return (
        <article
            className="glass-v2 hover-lift-v2 rounded-[28px] overflow-hidden group relative"
            data-cursor="hover"
        >
            {/* Hover ambient glow */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(500px circle at 50% 0%, hsl(190 90% 50% / 0.14), transparent 65%)",
                }}
            />

            <Link
                to={localizedPath(`/blog/${post.slug}`)}
                className="relative flex flex-col sm:flex-row-reverse h-full"
            >
                {/* Image side — sits at the start in the RTL flex row (right edge of the card) */}
                <div className="relative shrink-0 sm:w-[190px] md:w-[210px] aspect-[4/3] sm:aspect-auto overflow-hidden">
                    {post.featuredImage ? (
                        <img
                            src={post.featuredImage}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(.23,1,.32,1)] group-hover:scale-[1.06]"
                        />
                    ) : (
                        <div
                            className="absolute inset-0 flex items-center justify-center"
                            style={{
                                background:
                                    "linear-gradient(135deg, hsl(260 90% 65% / 0.25), hsl(190 90% 50% / 0.15))",
                            }}
                        >
                            <span className="font-v2-display text-6xl text-white/20 select-none">
                                م
                            </span>
                        </div>
                    )}
                    {/* Subtle fade over image toward the text side */}
                    <div
                        aria-hidden
                        className="absolute inset-0 bg-gradient-to-l from-background/60 via-transparent to-transparent sm:bg-gradient-to-l"
                    />
                </div>

                {/* Text side */}
                <div className="flex-1 p-6 md:p-7 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                        <span className="font-v2-label text-[10px] px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary">
                            {categoryName}
                        </span>
                        <span className="inline-flex items-center gap-1 font-v2-label text-[10px] text-white/40">
                            <Clock className="w-3 h-3" />
                            {post.readTime} {t("blog_home.read_time_suffix")}
                        </span>
                    </div>

                    <h3 className="font-v2-display text-lg md:text-xl text-white mb-3 leading-snug line-clamp-2 transition-colors duration-300 group-hover:text-white/90">
                        {post.title}
                    </h3>

                    {post.excerpt && (
                        <p className="text-sm text-muted-foreground leading-relaxed font-medium line-clamp-2 mb-4 flex-1">
                            {post.excerpt}
                        </p>
                    )}

                    <div className="mt-auto flex items-center justify-between gap-2 pt-3 border-t border-white/5">
                        <div className="flex items-center gap-2.5 min-w-0">
                            {post.author?.avatar ? (
                                <img
                                    src={post.author.avatar}
                                    alt=""
                                    loading="lazy"
                                    className="w-7 h-7 rounded-full object-cover border border-white/10 shrink-0"
                                />
                            ) : (
                                <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                                    <span className="text-[10px] text-white/60">
                                        {post.author?.name?.[0] ?? "م"}
                                    </span>
                                </div>
                            )}
                            <span className="text-xs text-white/70 font-bold truncate">
                                {post.author?.name ?? t("blog_home.default_author")}
                            </span>
                        </div>
                        <span className="font-v2-label text-[10px] text-white/40 shrink-0">
                            {formatDate(post.publishedAt)}
                        </span>
                    </div>
                </div>
            </Link>
        </article>
    );
};

const BlogSkeleton = () => (
    <div className="glass-v2 rounded-[28px] overflow-hidden flex flex-col sm:flex-row-reverse animate-pulse">
        <div className="sm:w-[190px] md:w-[210px] aspect-[4/3] sm:aspect-auto bg-white/5 shrink-0" />
        <div className="flex-1 p-6 flex flex-col gap-3">
            <div className="flex gap-2">
                <div className="h-5 w-16 rounded-full bg-white/5" />
                <div className="h-5 w-12 rounded-full bg-white/5" />
            </div>
            <div className="h-4 w-4/5 bg-white/5 rounded" />
            <div className="h-4 w-3/5 bg-white/5 rounded" />
            <div className="h-3 w-full bg-white/5 rounded mt-2" />
            <div className="h-3 w-5/6 bg-white/5 rounded" />
            <div className="mt-auto h-3 w-1/3 bg-white/5 rounded" />
        </div>
    </div>
);

const BlogRow = ({
    post,
    index,
    isLoading,
}: {
    post: BlogPostSummary | undefined;
    index: number;
    isLoading: boolean;
}) => {
    const isEven = index % 2 === 0; // 0, 2 → right; 1, 3 → left
    const num = (index + 1).toString().padStart(2, "0");
    const rowRef = useRef<HTMLLIElement>(null);
    const { y, x, scale } = useTimelineRowMotion(rowRef, isEven);

    return (
        <motion.li
            ref={rowRef}
            style={{ y, x, scale }}
            className="relative md:grid md:grid-cols-2 md:gap-10 lg:gap-14 items-center"
        >
            {/* Node with distinct diamond/square style (rounded-2xl — differs from round nodes elsewhere) */}
            <div
                aria-hidden
                className="hidden md:flex absolute right-1/2 top-10 translate-x-1/2 w-14 h-14 rotate-45 items-center justify-center z-10 rounded-2xl"
                style={{
                    background: "var(--gradient-accent-v2)",
                    boxShadow: "0 0 28px hsl(190 90% 50% / 0.55)",
                }}
            >
                <span className="font-v2-label text-[11px] text-white -rotate-45">
                    {num}
                </span>
            </div>

            {/* Card */}
            <div
                className={`${isEven ? "md:col-start-1" : "md:col-start-2"}`}
            >
                {isLoading || !post ? <BlogSkeleton /> : <BlogCard post={post} />}
            </div>
        </motion.li>
    );
};

const BlogTimeline = () => {
    const { t } = useTranslation();
    const { lang } = useLanguage();
    const localizedPath = useLocalizedPath();
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    });
    const spineScale = useTransform(scrollYProgress, [0.1, 0.9], [0, 1]);

    const { data, isLoading, isError } = useQuery({
        queryKey: ["home-blog", HOME_BLOG_LIMIT, lang],
        queryFn: async () => {
            const res = await blogApi.getPosts({ limit: HOME_BLOG_LIMIT, page: 1, lang });
            return res.data.data;
        },
        staleTime: 15 * 60 * 1000,
        retry: 1,
    });

    const featured = data?.featuredPost ?? data?.posts?.[0];
    const rest = (data?.posts ?? []).filter((p) => p.id !== featured?.id);
    const posts: BlogPostSummary[] = featured
        ? [featured, ...rest].slice(0, HOME_BLOG_LIMIT)
        : (data?.posts ?? []).slice(0, HOME_BLOG_LIMIT);

    // If API failed or returned nothing, don't render the section at all
    if (!isLoading && (isError || posts.length === 0)) {
        return null;
    }

    const rows: (BlogPostSummary | undefined)[] = isLoading
        ? Array.from({ length: HOME_BLOG_LIMIT })
        : [...posts, ...Array.from({ length: Math.max(0, HOME_BLOG_LIMIT - posts.length) })];

    return (
        <section
            ref={containerRef}
            className="py-20 md:py-32 relative"
            id="blog"
        >
            <div className="container mx-auto px-6 relative">
                {/* Header */}
                <div className="max-w-3xl mx-auto text-center mb-20">
                    <p className="font-v2-label text-xs text-primary/80 mb-4">{t("blog_home.eyebrow")}</p>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("blog_home.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("blog_home.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("blog_home.subtitle")}
                    </p>
                </div>

                {/* Timeline */}
                <div className="relative max-w-6xl mx-auto">
                    {/* Base spine */}
                    <div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px bg-white/[0.06]"
                    />
                    {/* Progressive cyan→violet fill (reversed to differ from sibling timelines) */}
                    <motion.div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px origin-top"
                        style={{
                            scaleY: spineScale,
                            background:
                                "linear-gradient(180deg, hsl(190 90% 50% / 0.9), hsl(260 90% 65% / 0.9), hsl(190 90% 50% / 0.4))",
                        }}
                    />

                    <ol className="space-y-14 md:space-y-16">
                        {rows.map((post, i) => (
                            <BlogRow
                                key={post?.id ?? i}
                                post={post}
                                index={i}
                                isLoading={isLoading}
                            />
                        ))}
                    </ol>
                </div>

                {/* Footer CTA */}
                <div className="mt-16 flex items-center justify-center">
                    <Link to={localizedPath("/blog")} data-cursor="hover">
                        <Button
                            className="rounded-full text-white font-bold gap-2 h-11 border-0"
                            style={{
                                background: "var(--gradient-accent-v2)",
                                boxShadow: "0 10px 28px -10px hsl(260 100% 55% / 0.5)",
                            }}
                        >
                            {t("blog_home.see_all")}
                            <ArrowLeft size={16} />
                        </Button>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default BlogTimeline;
